import React, { useState, useEffect, useCallback } from 'react';
import { FlagIcon } from '@heroicons/react/24/outline';
import StepHeader from './StepHeader';
import StepNavigation from './StepNavigation';

const RegisterNavUpdater = ({ registerNav, onNext, onBack, canNext, canBack }) => {
  const prevRef = React.useRef(null);
  const onNextRef = React.useRef(onNext);
  const onBackRef = React.useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const payload = {
      onNext: onNextRef.current,
      onBack: onBackRef.current,
      canNext,
      canBack,
    };
    const prev = prevRef.current;
    const changed =
      !prev ||
      prev.onNext !== payload.onNext ||
      prev.onBack !== payload.onBack ||
      prev.canNext !== payload.canNext ||
      prev.canBack !== payload.canBack;
    if (changed) {
      registerNav(payload);
      prevRef.current = payload;
    }
  }, [registerNav, canNext, canBack]);
  return null;
};

// This step focuses on choosing the algorithm preference (prompt_type)
const Step5AlgorithmPreferences = ({ promptType, onComplete, onBack, registerNav }) => {
  const availableValues = new Set(['crew_consistent', 'crew_mixing']);
  const initialSelected =
    promptType && availableValues.has(promptType) ? promptType : 'crew_consistent';
  const [selectedPromptType, setSelectedPromptType] = useState(initialSelected);
  const onCompleteRef = React.useRef(onComplete);

  // Update ref when onComplete changes
  React.useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const goalOptions = [
    {
      value: 'crew_consistent',
      label: 'Keep crews consistent',
      description: 'Maintain stable crew compositions across sessions to build cohesion.',
    },
    {
      value: 'crew_mixing',
      label: 'Mix crews for variety',
      description: 'Rotate rowers between crews to increase variety and cross-crew experience.',
    },
    {
      value: 'availability_preference',
      label: 'Respect preferred availability',
      description: 'Prefer sessions that best match rowers’ stated availability and preferences.',
    },
    {
      value: 'consistent_times_crew',
      label: 'Consistent training times (per crew)',
      description: 'Aim for similar training times for each crew across the planning window.',
    },
    {
      value: 'coach_consistent',
      label: 'Keep coach-crew pairings consistent',
      description: 'Maintain consistent coach assignments with specific crews where possible.',
    },
    {
      value: 'coach_mixing',
      label: 'Rotate coaches across crews',
      description: 'Distribute coaching time across different crews for broader coverage.',
    },
  ];

  const handleContinue = useCallback(() => {
    onCompleteRef.current(selectedPromptType);
  }, [selectedPromptType]);

  return (
    <div className="space-y-6">
      {registerNav && (
        <RegisterNavUpdater
          registerNav={registerNav}
          onNext={handleContinue}
          onBack={onBack}
          canNext={true}
          canBack={true}
        />
      )}
      <StepHeader
        icon={FlagIcon}
        title="Algorithm Preferences"
        description="Select the optimization goal. The algorithm will run with this objective and adjust its behavior accordingly."
      />

      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">Optimization Goal</h3>
          <p className="mt-1 text-sm text-gray-600">
            Pick the strategy that best fits your planning intent. You can review your choice in the
            next step.
          </p>
        </div>

        <div className="divide-y divide-gray-200">
          {goalOptions.map((opt) => {
            const isAvailable = availableValues.has(opt.value);
            const isSelected = selectedPromptType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={!isAvailable}
                onClick={() => {
                  if (isAvailable) setSelectedPromptType(opt.value);
                }}
                className={`w-full px-6 py-4 text-left focus:outline-none ${
                  isAvailable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-60'
                } ${isSelected ? 'border-l-4 border-blue-500 bg-blue-50' : ''}`}
              >
                <div className="flex items-start">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{opt.label}</h4>
                    <p className="mt-1 text-sm text-gray-600">{opt.description}</p>
                  </div>
                  {isAvailable ? (
                    isSelected && (
                      <span className="ml-4 inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        Selected
                      </span>
                    )
                  ) : (
                    <span className="ml-4 inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      Coming soon
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <StepNavigation onBack={onBack} onContinue={handleContinue} backLabel="Back" />
    </div>
  );
};

export default Step5AlgorithmPreferences;
